import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/page-header';
import { Upload, Plus } from 'lucide-react';
import { Invoice, InvoiceStatus } from '../types';
import {
  InvoiceFilters,
  InvoiceTable,
  InvoiceUpdateDialog,
  InvoiceHistoryDialog,
  BulkUpdateDialog,
  InvoicePagination,
  StatusChangeNoteDialog,
} from '../components/invoices';
import {
  useInvoices,
  useUpdateInvoice,
  useDeleteInvoice,
  useBulkUpdateInvoiceStatus,
  useInvoiceAuditLogs,
  useUsers,
  useDebounce,
  useIsAdmin,
  useIsManager,
} from '../hooks';

const Invoices: React.FC = () => {
  const { t } = useTranslation('invoices');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { isManager, isLoading: managerLoading } = useIsManager();
  const canManageInvoices = isAdmin || isManager;
  const effectiveRole = isAdmin ? 'admin' : isManager ? 'manager' : 'user';
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '__all__');
  const [monthFilter, setMonthFilter] = useState<string>(searchParams.get('month') || '__all__');
  const [memberNameFilter, setMemberNameFilter] = useState<string>(searchParams.get('memberName') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Dialog state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [pendingQuickChange, setPendingQuickChange] = useState<{
    invoiceId: string;
    targetStatus: InvoiceStatus;
  } | null>(null);

  // Update form state
  const [status, setStatus] = useState<InvoiceStatus>(InvoiceStatus.PENDING);
  const [reviewerComments, setReviewerComments] = useState('');
  const [overrideTotal, setOverrideTotal] = useState<string>('');

  // Bulk update state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<InvoiceStatus>(InvoiceStatus.APPROVED);

  // Sync filters with URL params
  useEffect(() => {
    const status = searchParams.get('status') || '__all__';
    const month = searchParams.get('month') || '__all__';
    const name = searchParams.get('memberName') || '';
    setStatusFilter(status);
    setMonthFilter(month);
    setMemberNameFilter(name);
    setCurrentPage(1);
  }, [searchParams]);

  // Debounce the member name filter to avoid excessive API calls
  const debouncedMemberName = useDebounce(memberNameFilter, 300);

  // React Query hooks
  const { data, isLoading } = useInvoices({
    status: statusFilter !== '__all__' ? statusFilter : undefined,
    month: monthFilter !== '__all__' ? monthFilter : undefined,
    memberName: debouncedMemberName || undefined,
    page: currentPage,
    limit: itemsPerPage,
  });

  // Fetch users for contract rate display (admins only)
  const { data: users = [] } = useUsers(false);

  const updateInvoice = useUpdateInvoice();
  const deleteInvoice = useDeleteInvoice();
  const bulkUpdate = useBulkUpdateInvoiceStatus();

  // Audit logs query (only when dialog is open)
  const { data: auditLogs = [], isLoading: loadingHistory } = useInvoiceAuditLogs(
    selectedInvoice?.id || ''
  );

  const invoices = data?.invoices || [];
  const totalInvoices = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  // Build URL search params from the current filter values
  const buildFilterParams = (status: string, month: string, memberName: string) => {
    const params = new URLSearchParams();
    if (status && status !== '__all__') params.append('status', status);
    if (month && month !== '__all__') params.append('month', month);
    if (memberName) params.append('memberName', memberName);
    return params;
  };

  // Filter handlers
  const handleClearFilters = () => {
    setStatusFilter('__all__');
    setMonthFilter('__all__');
    setMemberNameFilter('');
    setSearchParams(new URLSearchParams());
  };

  // Update dialog handlers
  const isOwnInvoice = (invoice: Invoice) => user?.id === invoice.userId;

  const handleUpdateClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    const canChangeThisStatus = isAdmin || (isManager && !isOwnInvoice(invoice));
    const invoiceStatus = !canChangeThisStatus && invoice.status === InvoiceStatus.PAID
      ? InvoiceStatus.APPROVED
      : invoice.status;
    setStatus(invoiceStatus);
    setReviewerComments(invoice.reviewerComments || '');
    setOverrideTotal(invoice.overrideTotal?.toString() || '');
    setUpdateDialogOpen(true);
  };

  const handleUpdateSubmit = async () => {
    if (!selectedInvoice) return;

    try {
      const updateData: any = {};

      const canChangeThisStatus = canManageInvoices && !isOwnInvoice(selectedInvoice);
      if (!canChangeThisStatus) {
        updateData.notes = reviewerComments;
      } else {
        updateData.status = status;
        updateData.reviewerComments = reviewerComments;
        if (isAdmin && overrideTotal && overrideTotal.trim() !== '') {
          updateData.overrideTotal = parseFloat(overrideTotal);
        }
      }

      await updateInvoice.mutateAsync({ id: selectedInvoice.id, data: updateData });
      setUpdateDialogOpen(false);
    } catch (error) {
      console.error('Error updating invoice:', error);
    }
  };

  // Delete handler
  const handleDelete = async (id: string) => {
    if (!window.confirm(t('deleteConfirm'))) {
      return;
    }

    try {
      await deleteInvoice.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };

  // History dialog handler
  const handleHistoryClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setHistoryDialogOpen(true);
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedInvoiceIds(invoices.map((inv) => inv.id));
    } else {
      setSelectedInvoiceIds([]);
    }
  };

  const handleSelectInvoice = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedInvoiceIds((prev) => [...prev, id]);
    } else {
      setSelectedInvoiceIds((prev) => prev.filter((invId) => invId !== id));
    }
  };

  // Bulk update handlers
  const handleBulkUpdateClick = () => {
    if (selectedInvoiceIds.length === 0) {
      alert('Please select at least one invoice');
      return;
    }
    setBulkUpdateDialogOpen(true);
  };

    const handleBulkUpdateSubmit = async (bulkReviewerComments?: string) => {
      try {
        await bulkUpdate.mutateAsync({
          ids: selectedInvoiceIds,
          status: bulkStatus,
          ...(bulkReviewerComments && { reviewerComments: bulkReviewerComments }),
        });
        setBulkUpdateDialogOpen(false);
        setSelectedInvoiceIds([]);
      } catch (error) {
        console.error('Error bulk updating invoices:', error);
      }
    };

    // Quick status change handler (for individual invoice status change from dropdown)
    const handleQuickStatusChange = (invoiceId: string, newStatus: InvoiceStatus) => {
      if (newStatus === InvoiceStatus.UNDER_REVIEW || newStatus === InvoiceStatus.REJECTED) {
        setPendingQuickChange({ invoiceId, targetStatus: newStatus });
        setNoteDialogOpen(true);
      } else {
        performQuickStatusUpdate(invoiceId, newStatus);
      }
    };

    const performQuickStatusUpdate = async (
      invoiceId: string,
      newStatus: InvoiceStatus,
      reviewerComment?: string
    ) => {
      try {
        const data: { status: InvoiceStatus; reviewerComments?: string } = { status: newStatus };
        if (reviewerComment) {
          data.reviewerComments = reviewerComment;
        }
        await updateInvoice.mutateAsync({ id: invoiceId, data });
      } catch (error) {
        console.error('Error updating invoice status:', error);
      }
    };

    const handleNoteDialogConfirm = async (comment: string) => {
      if (pendingQuickChange) {
        await performQuickStatusUpdate(
          pendingQuickChange.invoiceId,
          pendingQuickChange.targetStatus,
          comment || undefined
        );
      }
      setNoteDialogOpen(false);
      setPendingQuickChange(null);
    };

    return (
    <div className="space-y-6" data-testid="invoices-page">
      <PageHeader title={t('title')} description={t('upload.description')} testId="invoices-header">
        <div className="flex gap-2">
          {canManageInvoices && selectedInvoiceIds.length > 0 && (
            <Button onClick={handleBulkUpdateClick} variant="secondary" data-testid="invoices-button-bulk-update">
              {t('bulk.selectedCount', { count: selectedInvoiceIds.length })}
            </Button>
          )}
          <Button onClick={() => navigate('/invoices/upload')} variant="outline" data-testid="invoices-button-upload">
            <Upload className="mr-2 h-4 w-4" />
            {t('uploadInvoice')}
          </Button>
          <Button onClick={() => navigate('/invoices/generate')} data-testid="invoices-button-generate">
            <Plus className="mr-2 h-4 w-4" />
            {t('generator.title')}
          </Button>
        </div>
      </PageHeader>

      <InvoiceFilters
        statusFilter={statusFilter}
        monthFilter={monthFilter}
        memberNameFilter={memberNameFilter}
        onStatusChange={(value) => {
          setStatusFilter(value);
          setSearchParams(buildFilterParams(value, monthFilter, memberNameFilter));
        }}
        onMonthChange={(value) => {
          setMonthFilter(value);
          setSearchParams(buildFilterParams(statusFilter, value, memberNameFilter));
        }}
        onMemberNameChange={(value) => {
          setMemberNameFilter(value);
          setSearchParams(buildFilterParams(statusFilter, monthFilter, value), { replace: true });
        }}
        onClearFilters={handleClearFilters}
      />

            <InvoiceTable
              invoices={invoices}
              loading={isLoading || adminLoading || managerLoading}
              userRole={effectiveRole}
              currentUserId={user?.id}
              users={users}
              selectedInvoiceIds={selectedInvoiceIds}
              onSelectAll={handleSelectAll}
              onSelectInvoice={handleSelectInvoice}
              onUpdateInvoice={handleUpdateClick}
              onDeleteInvoice={handleDelete}
              onViewHistory={handleHistoryClick}
              onStatusChange={handleQuickStatusChange}
            />

      <InvoicePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalInvoices}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      <InvoiceUpdateDialog
        open={updateDialogOpen}
        invoice={selectedInvoice}
        userRole={selectedInvoice && isManager && isOwnInvoice(selectedInvoice) ? 'user' : effectiveRole}
        status={status}
        reviewerComments={reviewerComments}
        overrideTotal={overrideTotal}
        onOpenChange={setUpdateDialogOpen}
        onStatusChange={setStatus}
        onCommentsChange={setReviewerComments}
        onOverrideTotalChange={setOverrideTotal}
        onSubmit={handleUpdateSubmit}
        isSubmitting={updateInvoice.isPending}
      />

      <InvoiceHistoryDialog
        open={historyDialogOpen}
        invoice={selectedInvoice}
        auditLogs={auditLogs}
        loading={loadingHistory}
        onOpenChange={setHistoryDialogOpen}
      />

      <BulkUpdateDialog
        open={bulkUpdateDialogOpen}
        selectedCount={selectedInvoiceIds.length}
        status={bulkStatus}
        onOpenChange={setBulkUpdateDialogOpen}
        onStatusChange={setBulkStatus}
        onSubmit={handleBulkUpdateSubmit}
        isSubmitting={bulkUpdate.isPending}
        userRole={effectiveRole}
      />

      <StatusChangeNoteDialog
        open={noteDialogOpen}
        onOpenChange={(open) => {
          setNoteDialogOpen(open);
          if (!open) setPendingQuickChange(null);
        }}
        targetStatus={pendingQuickChange?.targetStatus ?? null}
        onConfirm={handleNoteDialogConfirm}
      />
    </div>
  );
};

export default Invoices;
